import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'sharedUi/Button';
import { integrationAccountService, type IntegrationTestResult } from '../../services/integration-account.service';
import { extractErrorMessage } from '../../utils/error-handler';

const input =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

/** What Test means on each channel, said before it is pressed. */
const WHAT_TEST_DOES: Record<string, string> = {
  Email: 'Signs in to the SMTP server with this project’s saved settings.',
  Whatsapp: 'Asks the gateway whether this project’s instance is authenticated and connected.',
};

/**
 * Test — and, for OAuth email accounts, Connect / Authorize — for the integration
 * being edited. Both act on what is SAVED (the authorisation even leaves the
 * browser), so the panel says to save first. Which actions exist comes from the
 * channel's own description, never from a list kept here.
 */
const IntegrationAccountActions = ({ channel, appId, actions, authType }: {
  channel: string;
  appId?: string;
  actions: string[];
  /** Email only: Connect / Authorize is meaningless unless it is an OAuth account. */
  authType?: string;
}) => {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<'' | 'test' | 'connect'>('');
  const [result, setResult] = useState<IntegrationTestResult | null>(null);
  const [sendTo, setSendTo] = useState('');

  const isEmail = channel === 'Email';
  const canTest = actions.includes('test');
  const canAuthorize = actions.includes('oauth') && (authType || '').toLowerCase() === 'oauth2';

  // Back from Google / Microsoft: the callback put the outcome in the query string.
  useEffect(() => {
    const outcome = params.get('emailOAuth');
    if (!outcome) return;
    setResult({ ok: outcome === 'connected', stage: 'login', message: params.get('message') || '' });
    const next = new URLSearchParams(params);
    ['emailOAuth', 'message', 'account'].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!canTest && !canAuthorize) return null;

  const test = async () => {
    setBusy('test');
    setResult(null);
    try {
      setResult(await integrationAccountService.test(
        channel, appId, isEmail ? sendTo.trim() || undefined : undefined));
    } catch (error) {
      setResult({ ok: false, stage: 'configuration', message: extractErrorMessage(error, 'Test failed') });
    } finally {
      setBusy('');
    }
  };

  const connect = async () => {
    setBusy('connect');
    setResult(null);
    try {
      const { authorizeUrl } = await integrationAccountService.startOAuth(window.location.href, appId);
      window.location.assign(authorizeUrl);
    } catch (error) {
      setResult({ ok: false, stage: 'configuration', message: extractErrorMessage(error, 'Could not start authorisation') });
      setBusy('');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-3"
      data-testid="IntegrationAccount.Actions">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Check this connection</h2>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        {WHAT_TEST_DOES[channel] || 'Checks this project’s saved settings for the channel.'}
        {' '}Tests what is saved, so save your changes first.
        {canAuthorize && ' Connect / Authorize sends you to the provider to authorise the mailbox.'}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        {isEmail && (
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              Send a test email to (optional)
            </label>
            <input className={input} value={sendTo} placeholder="someone@example.com"
              onChange={(e) => setSendTo(e.target.value)} data-testid="IntegrationAccount.SendTo" />
          </div>
        )}
        {canTest && (
          <Button label="Test" icon="pi pi-check-circle" severity="secondary" size="small"
            loading={busy === 'test'} disabled={!!busy} onClick={test}
            testId="IntegrationAccount.Test" />
        )}
        {canAuthorize && (
          <Button label="Connect / Authorize" icon="pi pi-external-link" size="small"
            loading={busy === 'connect'} disabled={!!busy} onClick={connect}
            testId="IntegrationAccount.Connect" />
        )}
      </div>
      {result && (
        <div
          className={`mt-3 rounded border p-2 text-sm break-words ${result.ok
            ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200'
            : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'}`}
          data-testid="IntegrationAccount.Result"
        >
          <i className={`pi ${result.ok ? 'pi-check-circle' : 'pi-times-circle'} mr-2`} />
          {result.message}
        </div>
      )}
    </div>
  );
};

export default IntegrationAccountActions;
